<template>
  <b-nav-item-dropdown
    class="dropdown-notification mr-75"
    menu-class="dropdown-menu-media dropdown-menu-right"
    left
  >
    <template #button-content>
      <feather-icon
        :badge="badgeCount"
        badge-classes="bg-danger"
        class="text-body"
        icon="MapIcon"
        size="21"
        v-b-tooltip.hover.button.viewport="`${$t('assigned_regions.title')}`"
      />
    </template>
    <li
      class="dropdown-menu-header"
      @click.stop
      @mousedown.stop
      @mouseup.stop
    >
      <div class="dropdown-header d-flex">
        <h4 class="notification-title mb-0 mr-auto">
          <router-link :to="{ name: routeName }">
            <feather-icon icon="LinkIcon" />
            <span class="align-middle ml-50">
              {{ $t("assigned_regions.title") }}
            </span>
          </router-link>
        </h4>
        <div>
          <b-badge pill variant="light-primary">
            {{ badgeCount }} {{ $t("assigned_regions.regions") }}
          </b-badge>
        </div>
      </div>
    </li>
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="scrollable-container media-list scroll-area"
      tagname="li"
    >
      <div
        class="px-1 py-1"
        @click.stop
        @mousedown.stop
        @mouseup.stop
      >
        <b-media class="mb-1">
          <template #aside>
            <b-avatar size="32">
              <span class="d-flex align-items-center">
                <feather-icon
                  icon="UserIcon"
                  size="16"
                />
              </span>
            </b-avatar>
          </template>
          <p class="media-heading mb-50">
            <span class="font-weight-bolder">
              {{ $t("assigned_regions.supervisor") }}:
              <small class="notification-text">
                {{ supervisorLabel }}
              </small>
            </span>
          </p>
          <div class="mt-50">
            <b-badge
              v-for="sector in displaySectors"
              :key="sector.id"
              pill
              variant="light-primary"
              class="mr-50 mb-50"
            >
              {{
                sector.name ||
                sector.description ||
                `#${sector.id}`
              }}
            </b-badge>
          </div>
        </b-media>
        <div
          v-if="isEditing"
          @click.stop
          @mousedown.stop
          @mouseup.stop
        >
          <label class="text-capitalize">
            {{ $t("assigned_regions.supervisor") }}
          </label>
          <users-select-paginated
            v-model="selectedSupervisor"
            endpoint="/v1/users"
            :limit="20"
            :multiple="false"
            :show-email-in-label="true"
            placeholder="Buscar supervisor..."
            class="mb-1"
          />
          <div class="d-flex align-items-center justify-content-between mb-50">
            <label class="text-capitalize mb-0">
              {{ $t("assigned_regions.regions") }}
            </label>
            <b-form-checkbox
              :checked="allSectorsSelected"
              :indeterminate="someSectorsSelected"
              :disabled="!sectorOptions.length"
              class="mb-0"
              @change="toggleAllSectors"
            >
              Seleccionar todos
            </b-form-checkbox>
          </div>
          <v-select
            v-model="selectedSectorIds"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="sectorOptions"
            :reduce="getSectorOptionValue"
            :close-on-select="false"
            multiple
            class="mb-1"
          >
            <template #no-options>
              No se encontraron regiones
            </template>
          </v-select>
        </div>
      </div>
    </vue-perfect-scrollbar>
    <li
      class="dropdown-menu-footer p-1"
      @click.stop
      @mousedown.stop
      @mouseup.stop
    >
      <div v-if="!isEditing">
        <b-button
          variant="primary"
          block
          @click.stop="startEditing"
        >
          {{ $t("assigned_regions.change") }}
        </b-button>
      </div>
      <div
        v-else
        class="d-flex"
      >
        <b-button
          variant="primary"
          block
          class="mr-50"
          :disabled="isSaving"
          @click.stop="saveChange"
        >
          <b-spinner
            v-if="isSaving"
            small
            class="mr-50"
          />
          {{ $t("actions.save") }}
        </b-button>
        <b-button
          variant="outline-secondary"
          block
          :disabled="isSaving"
          @click.stop="cancelEditing"
        >
          {{ $t("actions.cancel") }}
        </b-button>
      </div>
    </li>
  </b-nav-item-dropdown>
</template>
<script>
import {
  BNavItemDropdown,
  BBadge,
  BButton,
  BMedia,
  BAvatar,
  BFormCheckbox,
  BSpinner,
} from "bootstrap-vue";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import {
  computed,
  ref,
  onMounted,
  watch,
} from "@vue/composition-api";
import store from "@/store";
import vSelect from "vue-select";
import { decryptData } from "@/utils/decryptUserDataUtils";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

import UsersSelectPaginated from "@/components/UsersSelectPaginated.vue";
export default {
  components: {
    BNavItemDropdown,
    VuePerfectScrollbar,
    BBadge,
    BButton,
    BMedia,
    BAvatar,
    BFormCheckbox,
    BSpinner,
    vSelect,
    UsersSelectPaginated,
  },
  props: {
    routeName: {
      type: String,
      default: "user_assigned_regions",
    },
  },
  setup() {
    const toast = useToast();
    const isEditing = ref(false);
    const isSaving = ref(false);
    const selectedSupervisor = ref(null);
    const selectedSectorIds = ref([]);
    const currentUser = JSON.parse(
      decryptData(window.localStorage.userData)
    );
    const analystUserId = currentUser.id;
    const current = computed(() => {
      return (
        store.getters["user_assigned_region/getCurrent"] ||
        null
      );
    });
    const displaySectors = computed(() => {
      return Array.isArray(current.value?.sectors)
        ? current.value.sectors
        : [];
    });
    const badgeCount = computed(() => {
      return displaySectors.value.length;
    });
    const sectorOptions = computed(() => {
      return (
        store.getters["sector/getSelectedItems"] ||
        store.getters["sector/getItems"] ||
        []
      );
    });
    const getSectorOptionValue = option => {
      if (!option) {
        return null;
      }
      return option.value ?? option.id ?? null;
    };
    const allSectorIds = computed(() => {
      const ids = sectorOptions.value
        .map(getSectorOptionValue)
        .filter(
          id =>
            id !== null &&
            id !== undefined
        );
      return [...new Set(ids)];
    });
    const allSectorsSelected = computed(() => {
      if (!allSectorIds.value.length) {
        return false;
      }
      return allSectorIds.value.every(sectorId =>
        selectedSectorIds.value.some(
          selectedId =>
            Number(selectedId) === Number(sectorId)
        )
      );
    });
    const someSectorsSelected = computed(() => {
      if (!allSectorIds.value.length) {
        return false;
      }
      const selectedCount = allSectorIds.value.filter(
        sectorId =>
          selectedSectorIds.value.some(
            selectedId =>
              Number(selectedId) === Number(sectorId)
          )
      ).length;
      return (
        selectedCount > 0 &&
        selectedCount < allSectorIds.value.length
      );
    });
    const toggleAllSectors = checked => {
      if (checked) {
        selectedSectorIds.value = [
          ...allSectorIds.value,
        ];
        return;
      }
      selectedSectorIds.value = [];
    };
    function buildUserOptionLabel(user) {
      if (!user) {
        return "";
      }
      const name =
        user.full_name ||
        user.name ||
        `${user.first_name || ""} ${user.last_name || ""}`.trim();
      const username =
        user.user ||
        user.username;
      return [
        name,
        username,
        user.email,
      ]
        .filter(Boolean)
        .join(" | ");
    }
    const normalizeSupervisorOption = supervisor => {
      if (!supervisor) {
        return null;
      }
      return {
        ...supervisor,
        id: supervisor.id,
        value: supervisor.id,
        label:
          buildUserOptionLabel(supervisor) ||
          `Usuario #${supervisor.id}`,
      };
    };
    const supervisorLabel = computed(() => {
      const supervisor = current.value?.supervisor;
      if (!supervisor) {
        return "-";
      }
      return (
        buildUserOptionLabel(supervisor) ||
        `#${supervisor.id}`
      );
    });
    const selectedSupervisorId = computed(() => {
      if (
        selectedSupervisor.value === null ||
        selectedSupervisor.value === undefined
      ) {
        return null;
      }
      if (
        typeof selectedSupervisor.value === "number" ||
        typeof selectedSupervisor.value === "string"
      ) {
        return selectedSupervisor.value;
      }
      return (
        selectedSupervisor.value.id ??
        selectedSupervisor.value.value ??
        null
      );
    });
    const loadCurrent = () => {
      return store.dispatch(
        "user_assigned_region/getCurrent",
        {
          analyst_user_id: analystUserId,
        }
      );
    };
    const loadSectors = () => {
      return store.dispatch("sector/getAll", {
        per_page: 500,
        max: 500,
      });
    };
    const syncFormFromCurrent = () => {
      selectedSupervisor.value =
        normalizeSupervisorOption(
          current.value?.supervisor
        );
      selectedSectorIds.value =
        Array.isArray(current.value?.sectors)
          ? current.value.sectors
              .map(sector => sector.id)
              .filter(
                id =>
                  id !== null &&
                  id !== undefined
              )
          : [];
    };
    const showErrorToast = message => {
      toast({
        component: ToastificationContent,
        props: {
          title: message,
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
    };
    const showSuccessToast = message => {
      toast({
        component: ToastificationContent,
        props: {
          title: message,
          icon: "CheckIcon",
          variant: "success",
        },
      });
    };
    const startEditing = () => {
      syncFormFromCurrent();
      isEditing.value = true;
    };
    const cancelEditing = () => {
      syncFormFromCurrent();
      isEditing.value = false;
    };
    const saveChange = async () => {
      if (!selectedSupervisorId.value) {
        showErrorToast("Selecciona un supervisor");
        return;
      }
      if (!selectedSectorIds.value.length) {
        showErrorToast(
          "Selecciona al menos una región"
        );
        return;
      }
      isSaving.value = true;
      try {
        const response = await store.dispatch(
          "user_assigned_region/changeNow",
          {
            analyst_user_id: analystUserId,
            supervisor_user_id:
              selectedSupervisorId.value,
            sector_ids: selectedSectorIds.value,
          }
        );
        showSuccessToast(
          response?.message ||
          "Cambio realizado correctamente"
        );
        await loadCurrent();
        syncFormFromCurrent();
        isEditing.value = false;
      } catch (error) {
        console.error(
          "Error cambiando asignación:",
          error
        );
        const message =
          error?.response?.data?.message ||
          "Error cambiando asignación";
        showErrorToast(message);
      } finally {
        isSaving.value = false;
      }
    };
    onMounted(async () => {
      try {
        await Promise.all([
          loadSectors(),
          loadCurrent(),
        ]);
        syncFormFromCurrent();
      } catch (error) {
        console.error(
          "Error cargando asignación o regiones:",
          error
        );
        showErrorToast(
          "No fue posible cargar la asignación o las regiones"
        );
      }
    });
    watch(current, () => {
      if (!isEditing.value) {
        syncFormFromCurrent();
      }
    });
    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    };
    return {
      perfectScrollbarSettings,
      displaySectors,
      badgeCount,
      supervisorLabel,
      sectorOptions,
      getSectorOptionValue,
      selectedSupervisor,
      selectedSectorIds,
      allSectorsSelected,
      someSectorsSelected,
      toggleAllSectors,
      isEditing,
      isSaving,
      startEditing,
      cancelEditing,
      saveChange,
    };
  },
};
</script>
